import { colors, fontStacks } from '@ctw/config/zui';
import { type ClassName, twx } from '@ctw/shared/utils/tailwind';
import ReactJson, { type ThemeObject } from '@microlink/react-json-view';

// base16 slots as react-json-view uses them, filled from the zui palette.
const zuiJsonTheme: ThemeObject = {
  base00: 'transparent', // background
  base01: colors.background.disabled,
  base02: colors.divider.main, // indent guides
  base03: colors.content.subtle,
  base04: colors.content.disabled, // object sizes
  base05: colors.content.subtle,
  base06: colors.content.subtle,
  base07: colors.content.main, // keys and brackets
  base08: colors.error.text,
  base09: colors.primary.text, // strings
  base0A: colors.content.disabled, // null and undefined
  base0B: colors.info.text, // floats
  base0C: colors.content.subtle, // array indices
  base0D: colors.primary.main, // expanded icon
  base0E: colors.info.text, // booleans and collapsed icon
  base0F: colors.info.text, // integers
};

export interface JsonViewerProps {
  json: object;
  // Nesting depth expanded on first render; deeper levels start collapsed. Fully expanded if unset.
  expandDepth?: number;
  className?: ClassName;
  id?: string;
  testId?: string;
}

export const JsonViewer = ({ json, expandDepth, className, id, testId }: JsonViewerProps) => (
  <div
    id={id}
    data-testid={testId}
    className={twx(
      'overflow-auto rounded-lg border border-divider-main bg-background-hover p-4 text-sm',
      // Center the library's copy icon without growing the line, checkmark beside it, not over it.
      '[&_.copy-to-clipboard-container]:!align-middle [&_.copy-to-clipboard-container]:!leading-[0]',
      '[&_.copy-icon]:!inline-flex [&_.copy-icon]:!align-middle [&_.copy-icon+span]:!ml-1',
      // The library dims and letter-spaces keys.
      '[&_.object-key]:!tracking-normal [&_.object-key]:!opacity-100',
      className,
    )}
  >
    <ReactJson
      src={json}
      theme={zuiJsonTheme}
      name={false}
      collapsed={expandDepth ?? false}
      displayDataTypes={false}
      displayObjectSize={false}
      quotesOnKeys={false}
      iconStyle="triangle"
      indentWidth={2}
      collapseStringsAfterLength={120}
      style={{ fontFamily: fontStacks.mono.join(', ') }}
    />
  </div>
);
